import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, IconButton, Tab, Tabs, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';

import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import SearchCard, { fieldProps } from '../../components/common/list/SearchCard';
import TotalsLine from '../../components/common/list/TotalsLine';
import ResponsiveDataGrid from '../../components/common/datagrid/ResponsiveDataGrid';
import { listSaved, deleteSaved, copySaved } from '../../lib/api/report';

const VIEW_LABEL = { TABLE: '표', BAR: '막대', LINE: '선' };
const SHARE_LABEL = { PRIVATE: '나만', COMPANY: '회사 전체' };

/**
 * 보고서 목록(/reports) - 탭: 내 보고서 / 공유받은 보고서(계획 §6-1).
 * SearchCard는 "내 보고서 이름" 검색만 한다(템플릿·필드 검색이 아니다 - R10).
 */
const ReportListContainer = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState('mine'); // mine | shared
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nameWord, setNameWord] = useState('');
  const [searchWord, setSearchWord] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    listSaved()
      .then((res) => setAll(res.data || []))
      .catch(() => enqueueSnackbar('목록을 불러오지 못했습니다.', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredByTab = useMemo(
    () => all.filter((r) => (tab === 'mine' ? r.mine : !r.mine)),
    [all, tab],
  );

  const rows = useMemo(() => {
    const word = searchWord.trim();
    const list = word ? filteredByTab.filter((r) => r.rptNm?.includes(word)) : filteredByTab;
    return list.map((r) => ({ id: r.rptUuid, ...r }));
  }, [filteredByTab, searchWord]);

  const handleDelete = (rptUuid) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('이 보고서를 삭제하시겠습니까?')) return;
    deleteSaved(rptUuid)
      .then(() => {
        enqueueSnackbar('삭제했습니다.', { variant: 'success' });
        load();
      })
      .catch(() => enqueueSnackbar('삭제하지 못했습니다.', { variant: 'error' }));
  };

  const handleCopy = (rptUuid) => {
    copySaved(rptUuid)
      .then(() => {
        enqueueSnackbar('내 보고서로 복사했습니다.', { variant: 'success' });
        setTab('mine');
        load();
      })
      .catch(() => enqueueSnackbar('복사하지 못했습니다.', { variant: 'error' }));
  };

  const columns = [
    { field: 'rptNm', headerName: '보고서 이름', flex: 1.4, minWidth: 160 },
    { field: 'tmplNm', headerName: '템플릿', width: 120 },
    {
      field: 'viewTyp',
      headerName: '표현',
      width: 90,
      valueFormatter: (value) => VIEW_LABEL[value] || value,
    },
    {
      field: 'shareTyp',
      headerName: '공유 범위',
      width: 110,
      renderCell: (params) => (
        <Chip size="small" label={SHARE_LABEL[params.value] || params.value} variant="outlined" />
      ),
    },
    { field: 'ownerUsrNm', headerName: '소유자', width: 110 },
    { field: 'cngDt', headerName: '최종 수정일', width: 170 },
    {
      field: 'actions',
      headerName: '',
      width: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="보기">
            <IconButton size="small" onClick={() => navigate(`/reports/${params.row.rptUuid}`)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.mine && (
            <Tooltip title="편집">
              <IconButton size="small" onClick={() => navigate(`/reports/${params.row.rptUuid}/edit`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="내 보고서로 복사">
            <IconButton size="small" onClick={() => handleCopy(params.row.rptUuid)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.mine && (
            <Tooltip title="삭제">
              <IconButton size="small" onClick={() => handleDelete(params.row.rptUuid)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <BreadcrumbsContainer title="분석 보고서" icon="chart" />

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1.5 }}>
        <Tab value="mine" label="내 보고서" />
        <Tab value="shared" label="공유받은 보고서" />
      </Tabs>

      <SearchCard
        summary={nameWord ? `이름: ${nameWord}` : '전체'}
        onSubmit={(e) => {
          e.preventDefault();
          setSearchWord(nameWord);
        }}
        onReset={() => {
          setNameWord('');
          setSearchWord('');
        }}
        gridSx={{ gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gridTemplateAreas: { sm: '"name btns"' } }}
      >
        <Box sx={{ gridArea: 'name' }}>
          <TextField
            {...fieldProps}
            fullWidth
            label="보고서 이름"
            placeholder="내 보고서 이름으로 찾기"
            value={nameWord}
            onChange={(e) => setNameWord(e.target.value)}
          />
        </Box>
      </SearchCard>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1.5 }}>
        <TotalsLine
          items={[
            { label: '보고서', value: `${filteredByTab.length}개` },
            { label: '공유', value: `${all.filter((r) => r.shareTyp === 'COMPANY').length}개` },
          ]}
          sx={{ justifyContent: 'flex-start' }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/reports/new')}>
          새 보고서
        </Button>
      </Box>

      <Box sx={{ height: 560, width: '100%' }}>
        <ResponsiveDataGrid
          gridId="report-list"
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`/reports/${params.row.rptUuid}`)}
          mobileCard={{ title: 'rptNm', subtitle: 'tmplNm', badge: 'shareTyp', fields: ['ownerUsrNm', 'cngDt', 'viewTyp'] }}
        />
      </Box>
    </Box>
  );
};

export default ReportListContainer;
